import { objectToCamel } from "ts-case-convert";
import { initialAvailabilitiesState } from "../store/initialisation/availabilities";
import { IAvailabilities } from "../models";
import { initialPaginationState } from "../store/initialisation/pagination";
import { ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";
import { APP } from "../config/app";
import { RouteService, StorageService } from "../services";
import { Injectable } from "@angular/core";
import { deserializeAvailabilitiesFilters } from "../filters";

@Injectable({
  providedIn: 'root',
})
export class AvailabilitiesResolver {
  // Fingerprint of every input except page. Lets us tell a pure page change
  // from a filter change so the pagination only collapses to "currentPage ···"
  // when the totals are genuinely unknown.
  private lastFilterFingerprint: string | null = null;

  constructor(
    public storageService: StorageService,
    public routeService: RouteService
  ) {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async resolve(route: ActivatedRouteSnapshot, state?: RouterStateSnapshot) {
    this.storageService.setRouteName('services')
    initialAvailabilitiesState.isFetchingAvailabilities = true;
    initialAvailabilitiesState.value = [];

    const filters = deserializeAvailabilitiesFilters(route.queryParamMap);
    const filterFingerprint = JSON.stringify({
      routeId: route.queryParamMap.get('routeId') ?? route.paramMap.get('routeId'),
      setupId: route.queryParamMap.get('setupId') ?? route.paramMap.get('setupId'),
      size: route.queryParamMap.get('availabilitiesSize'),
      search: route.queryParamMap.get('availabilitiesSearch'),
      selectedStatus: filters.selectedStatus,
      bookings: filters.bookings,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      startTime: filters.startTime,
      endTime: filters.endTime,
    });
    if (filterFingerprint !== this.lastFilterFingerprint) {
      initialAvailabilitiesState.pagination.totalPages = 0;
      initialAvailabilitiesState.pagination.totalElements = 0;
    }
    this.lastFilterFingerprint = filterFingerprint;

    this.resolveAsync(route)
  }

  async resolveAsync(route: ActivatedRouteSnapshot) {
    const pageParam = route.queryParamMap.get('availabilitiesPage');
    const pageFromUrl = pageParam ? Number(pageParam) : null;
    const page = (pageFromUrl && !isNaN(pageFromUrl) && pageFromUrl > 0)
      ? pageFromUrl
      : (route.data['availabilities']?.pagination.actualPage || 1);
    const sizeParam = route.queryParamMap.get('availabilitiesSize');
    const sizeFromUrl = sizeParam ? Number(sizeParam) : null;
    const size = (sizeFromUrl && !isNaN(sizeFromUrl) && sizeFromUrl > 0)
      ? sizeFromUrl
      : APP.DEFAULT_PAGE_SIZE;
    const search = route.queryParamMap.get('availabilitiesSearch') ?? '';
    const filters = deserializeAvailabilitiesFilters(route.queryParamMap);
    const routeId = Number(route.queryParamMap.get('routeId') ?? route.paramMap.get('routeId'));
    const setupIdParam = route.queryParamMap.get('setupId') ?? route.paramMap.get('setupId');
    const setupId = setupIdParam ? Number(setupIdParam) : undefined;

    const {
      content,
      numberOfElements,
      totalElements,
      totalPages,
      first,
      last,
    } = await this.routeService.getRouteAvailabilities(
      routeId, page, size, search,
      filters.selectedStatus, filters.bookings,
      filters.fromDate as any, filters.toDate as any,
      filters.startTime as any, filters.endTime as any,
      setupId
    );
    return this.getData(
      content,
      numberOfElements,
      totalElements,
      totalPages,
      first,
      last,
      page,
      size,
    );
  }

  getData = (
    content: any,
    number_of_elements: any,
    total_elements: any,
    total_pages: any,
    first: any,
    last: any,
    page: any,
    size: number = APP.DEFAULT_PAGE_SIZE,
  ) => {
    const data = initialAvailabilitiesState;
    data.value = objectToCamel(content) as Array<IAvailabilities>;
    data.isFetchingAvailabilities = false;
    data.pagination = {...initialPaginationState};
    data.pagination.numberOfElements = number_of_elements;
    data.pagination.totalPages = total_pages;
    data.pagination.totalElements = total_elements;
    data.pagination.actualPage = page;
    data.pagination.first = first;
    data.pagination.last = last;
    data.pagination.size = size;
    return data;
  };
}
